/**
 * Orders API
 *
 * This file is now a wrapper around the orderService to maintain backward compatibility
 * while transitioning to the new architecture.
 */

import axios from 'axios';
import * as orderService from '../services/orderService';

// Legacy axios import kept for backward compatibility with existing code
import { getApiUrl, createApiRequestConfig, isRunningLocally } from '../utils/apiUtils';

// Re-export all methods from orderService
export const { getOrders } = orderService;
export const { createOrder } = orderService;
export const { updateOrder } = orderService;
export const { deleteOrder } = orderService;

// Add getPatientOrders as an alias for getOrders for compatibility
export const getPatientOrders = orderService.getOrders;

// Create a centralized API instance using the apiUtils configuration
const api = axios.create({
  baseURL: getApiUrl('patients'),
  ...createApiRequestConfig(),
});

// Response interceptor for handling fallback on network errors
api.interceptors.response.use(
  response => response, // Pass through successful responses
  async error => {
    const originalRequest = error.config;

    // Check if it's a network error and we haven't already retried
    if (error.code === 'ERR_NETWORK' && !originalRequest._retry) {
      console.log('Network error, attempting fallback using centralized API utility');
      originalRequest._retry = true; // Mark as retried to prevent infinite loops

      try {
        // Use the direct API URL for fallback
        const fallbackUrl = getApiUrl(originalRequest.url.replace(/^\//, ''));
        console.log(`Retrying request with fallback URL: ${fallbackUrl}`);

        // Preserve original method, data, headers etc., but use the full URL
        const retryConfig = {
          ...originalRequest,
          baseURL: '',
          url: fallbackUrl,
          _retry: true,
        };

        return await axios(retryConfig);
      } catch (retryError) {
        console.error('Fallback request failed:', retryError);
        return Promise.reject(retryError);
      }
    }

    // For other errors, reject with the original error
    return Promise.reject(error);
  }
);

// Export the api instance for legacy code
export { api };
